export type AppContext =
    | 'connect'
    | 'learn'
    | 'lead'
    | 'global'
    | 'members'
    | 'events'
    | 'resources'
    | 'concierge'
    | 'messages'
    | 'notifications'
    | 'unknown'

export interface NavItem {
    text: string
    key: string
    url: string | null
    context?: AppContext
    icon?: unknown
}

export interface UserProfile {
    id?: string
    preferredName: string
    firstName: string
    lastName: string
    profileImageUrl: string
    recordTypeName: string
}

export interface GlobalHeaderNavigateEvent {
    href: string | null
    key: string
    label: string
    context: AppContext | null
}

export interface GlobalHeaderProfileLoadedEvent {
    userId: string
    profile: UserProfile
}

export interface GlobalHeaderAuthErrorEvent {
    status: number
    message: string
}

export interface GlobalHeaderAvatarClickEvent {
    dropdownOpen: boolean
}

export interface GlobalHeaderSearchEvent {
    key: 'search'
}

export const USER_PROFILE_ATTRIBUTE = 'user-profile'

export const CONNECT_BASE_URL = import.meta.env.VITE_URL_CONNECT
export const DEFAULT_API_BASE_URL = import.meta.env.VITE_BASE_CONNECT_API
export const M2MX_PROFILE_URL = import.meta.env.VITE_URL_M2MX_PROFILE
export const M2MX_OVERVIEW_URL = import.meta.env.VITE_URL_M2MX_OVERVIEW
export const MEMBERS_URL = import.meta.env.VITE_URL_MEMBERS
export const CONCIERGE_URL = import.meta.env.VITE_URL_CONCIERGE
export const LEARN_URL = import.meta.env.VITE_URL_LEARN
export const LEAD_URL = import.meta.env.VITE_URL_LEAD
export const GLOBAL_URL = import.meta.env.VITE_URL_GLOBAL

function withTrailingSlash(url: string | undefined): string {
    return `${(url ?? '').replace(/\/+$/, '')}/`
}

function withoutSiteSegment(url: string | undefined): string {
    return withTrailingSlash(url).replace(/\/s\/$/, '/')
}

function withScheme(url: string): string {
    return /^[a-z][a-z0-9+.-]*:\/\//i.test(url) ? url : `https://${url}`
}

function originOf(currentUrl: string): string {
    try {
        return new URL(withScheme(currentUrl)).origin
    } catch {
        return currentUrl
    }
}

// First non-empty path segment of a configured base URL, e.g.
// "https://.../membercare/s/" -> "membercare". "" for a bare-domain base like
// LEARN_URL, which has no distinguishing path segment to check for.
function siteSegmentOf(base: string | undefined): string {
    if (!base) return ''
    try {
        return new URL(withScheme(base)).pathname.split('/').filter(Boolean)[0] ?? ''
    } catch {
        return ''
    }
}

function isOnSite(currentUrl: string, base: string | undefined): boolean {
    if (!base) return false

    const normalizedUrl = withScheme(currentUrl)
    const root = withoutSiteSegment(base).replace(/\/+$/, '')
    if (normalizedUrl === root || normalizedUrl.startsWith(`${root}/`)) return true

    const segment = siteSegmentOf(base)
    return segment !== '' && normalizedUrl.includes(`/${segment}`)
}

export const GLOBAL_HEADER_ITEMS = {
    primary: [
        { text: 'Connect', key: 'connect', url: `${CONNECT_BASE_URL}/#/home`, context: 'connect' },
        { text: 'Learn', key: 'learn', url: LEARN_URL, context: 'learn' },
        { text: 'Lead', key: 'lead', url: LEAD_URL, context: 'lead' },
    ] as NavItem[],
    secondary: [
        { text: 'Members', key: 'members', url: MEMBERS_URL, context: 'members' },
        { text: 'Events', key: 'events', url: `${CONNECT_BASE_URL}/#/events`, context: 'events' },
        { text: 'Resources', key: 'resources', url: `${CONNECT_BASE_URL}/#/resources`, context: 'resources' },
        { text: 'Member Care', key: 'concierge', url: `${withoutSiteSegment(CONCIERGE_URL)}`, context: 'concierge' },
    ] as NavItem[],
}

export const ICON_LINKS = {
    messages: {
        text: 'Messages',
        key: 'messages',
        url: `${CONNECT_BASE_URL}/#/messenger`,
        context: 'messages',
    } as NavItem,
    notifications: { text: 'Notifications', key: 'notifications', url: null, context: 'notifications' } as NavItem,
}

export function resolveLogoutUrl(currentUrl: string, appContext: readonly string[] = ['unknown']): string {
    const origin = originOf(currentUrl)
    const isMembers = appContext.includes('members') || isOnSite(currentUrl, MEMBERS_URL)
    const isConcierge = appContext.includes('concierge') || isOnSite(currentUrl, CONCIERGE_URL)

    if (isMembers) {
        return `${withTrailingSlash(MEMBERS_URL)}secur/logout.jsp`
    }

    if (isConcierge) {
        return `${withoutSiteSegment(CONCIERGE_URL)}secur/logout.jsp`
    }

    const isLearnLeadOrGlobal =
        appContext.includes('learn') || appContext.includes('lead') || appContext.includes('global') ||
        isOnSite(currentUrl, LEARN_URL) || isOnSite(currentUrl, LEAD_URL) || isOnSite(currentUrl, GLOBAL_URL)

    if (isLearnLeadOrGlobal) {
        return `${withTrailingSlash(origin)}auth/logout`
    }

    return `${CONNECT_BASE_URL}/#/logout?redirectUrl=${origin}`
}

export function getAccountLinks(
    currentUrl: string,
    appContext: readonly string[] = ['unknown'],
    icons?: Partial<Record<'myAccount' | 'myProfile' | 'm2mx' | 'logout', unknown>>,
): Record<string, NavItem> {
    return {
        myAccount: {
            text: 'My account',
            key: 'account',
            url: `${CONNECT_BASE_URL}/#/privacySettings/myAccount`,
            context: 'connect',
            icon: icons?.myAccount,
        },
        myProfile: {
            text: 'My profile',
            key: 'profile',
            url: `${CONNECT_BASE_URL}/#/myProfile`,
            context: 'connect',
            icon: icons?.myProfile,
        },
        m2mx: {
            text: 'M2Mx',
            key: 'm2mx',
            url: M2MX_OVERVIEW_URL,
            context: 'connect',
            icon: icons?.m2mx,
        },
        logout: {
            text: 'Log out',
            key: 'logout',
            url: resolveLogoutUrl(currentUrl, appContext),
            context: 'connect',
            icon: icons?.logout,
        },
    }
}

export const CONTEXT_TITLES: Record<AppContext, string> = {
    connect: 'Connect',
    learn: 'Learn',
    lead: 'Lead',
    global: 'Global',
    members: 'Members',
    events: 'Events',
    resources: 'Resources',
    concierge: 'Member Care',
    messages: 'Messages',
    notifications: 'Notifications',
    unknown: 'YPO',
}

/**
 * Custom Lit boolean attribute converter that handles string "false" passed via setAttribute().
 * Standard Lit `type: Boolean` only checks attribute presence, so `show-shadow="false"`
 * would still be treated as true. This converter explicitly maps the string "false" to false.
 */
export const booleanConverter = {
    fromAttribute(val: string | null): boolean {
        if (val === null) return false
        if (val === 'false') return false
        return true
    },
    toAttribute(val: boolean): string | null {
        return val ? '' : null
    },
}

export const countConverter = {
    fromAttribute(val: string | null): number {
        if (val === null || val === '' || val === 'undefined' || val === 'null') return 0
        const n = Number(val)
        return isNaN(n) ? 0 : Math.max(0, Math.trunc(n))
    },
    toAttribute(val: number): string {
        return String(Math.max(0, val))
    },
}

export function parseUserProfileAttribute(val: string | null): UserProfile | null {
    if (val === null || val === '' || val === 'undefined' || val === 'null') return null

    try {
        return JSON.parse(val) as UserProfile
    } catch (error) {
        console.warn(`[global-ypo-header] Invalid ${USER_PROFILE_ATTRIBUTE} JSON:`, error)
        return null
    }
}

export function serializeUserProfileAttribute(val: UserProfile | null): string | null {
    return val ? JSON.stringify(val) : null
}

export function emit<T>(element: HTMLElement, name: string, detail: T): void {
    element.dispatchEvent(
        new CustomEvent<T>(name, {
            detail,
            bubbles: true,
            composed: true,
        })
    )
}

// 'concierge' is the canonical key Salesforce is expected to send, but we accept
// 'membercare' too and normalize it to 'concierge' — so either value works no
// matter which one a given Salesforce page actually ends up sending.
const mapAppContextAlias: Partial<Record<string, AppContext>> = {
    membercare: 'concierge',
}

export function parseAppContextAttribute(val: string | null): AppContext[] {
    if (!val) return ['unknown']
    const parsed = val
        .split(',')
        .map(part => part.trim())
        .filter(Boolean)
        .map(part => mapAppContextAlias[part] ?? part) as AppContext[]
    return parsed.length ? parsed : ['unknown']
}

export function serializeAppContextAttribute(val: AppContext[]): string {
    return val.join(',')
}

export function parseBooleanAttribute(val: string | null): boolean {
    if (val === null) return false
    return val !== 'false'
}

declare global {
    interface Window {
        dataLayer: Array<Record<string, unknown>>
    }
}

export function gaDataLayerEvent(eventName: string, eventValue: string, extraData: Record<string, unknown> = {}): void {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
        event: eventName,
        event_value: eventValue,
        ...extraData,
    })
}
